<script setup>
import { computed } from 'vue'
import { Head, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'

defineOptions({ layout: PublicLayout })

const { t } = useI18n()
const props = defineProps({
  tours:     { type: Array,  default: () => [] },
  filters:   { type: Object, default: () => ({}) },
  base_path: { type: String, default: '/tours' }
})

const categories = computed(() => ['boat_trip', 'snorkel', 'island_hopping', 'adventure', 'cultural', 'city', 'other'])

function applyFilter(key, value) {
  router.get(props.base_path, { ...props.filters, [key]: value }, { preserveScroll: true })
}
</script>

<template>
  <Head>
    <title>{{ t('public.tours.head_title') }}</title>
    <meta
      name="description"
      :content="t('public.tours.head_description')"
    >
  </Head>

  <section class="catalog">
    <h1 class="catalog__title">
      {{ t('public.tours.title') }}
    </h1>
    <p class="catalog__lede">
      {{ t('public.tours.lede') }}
    </p>

    <div class="filters">
      <button
        :class="['chip', !filters.category && 'is-active']"
        @click="applyFilter('category', null)"
      >
        {{ t('public.tours.filters.all') }}
      </button>
      <button
        v-for="cat in categories"
        :key="cat"
        :class="['chip', filters.category === cat && 'is-active']"
        @click="applyFilter('category', cat)"
      >
        {{ t(`vendor_portal.tour.categories.${cat}`) }}
      </button>
    </div>

    <p
      v-if="tours.length === 0"
      class="empty"
    >
      {{ t('public.tours.empty') }}
    </p>

    <ul
      v-else
      class="grid"
    >
      <li
        v-for="tr in tours"
        :key="tr.id"
        class="card"
      >
        <a
          :href="`${base_path}/${tr.id}`"
          class="card__link"
        >
          <div class="card__photo">
            <img
              v-if="tr.photo_url"
              :src="tr.photo_url"
              :alt="tr.title"
            >
            <div
              v-else
              class="card__photo-placeholder"
            >{{ tr.title?.[0] }}</div>
          </div>
          <div class="card__body">
            <h3 class="card__title">{{ tr.title }}</h3>
            <p
              v-if="tr.summary"
              class="card__summary"
            >{{ tr.summary }}</p>
            <p class="card__meta">{{ t(`vendor_portal.tour.categories.${tr.category}`) }} · {{ Math.round(tr.duration_minutes / 60) }}h · <span class="card__price">{{ tr.price_adult }}</span></p>
          </div>
        </a>
      </li>
    </ul>
  </section>
</template>

<style scoped>
.catalog { max-width: 60rem; margin: 0 auto; padding: 2.5rem 1.5rem 4rem; }
.catalog__title { font-family: var(--font-display, var(--display)); font-size: clamp(1.75rem, 4vw, 2.25rem); margin: 0 0 0.5rem; color: var(--ink); font-weight: 700; }
.catalog__lede  { color: var(--inkSoft); margin: 0 0 1.5rem; line-height: 1.6; }
.filters { display: flex; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 1.5rem; }
.chip { padding: 0.4rem 0.85rem; border: 1px solid var(--line); background: var(--surface); border-radius: 999px; font-size: 0.875rem; color: var(--inkSoft); cursor: pointer; }
.chip.is-active { background: var(--accent); color: var(--accentInk); border-color: var(--accent); }
.empty { padding: 3rem 1rem; text-align: center; color: var(--muted); }
.grid { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr)); gap: 1rem; }
.card { background: var(--surface); border: 1px solid var(--line); border-radius: 0.75rem; overflow: hidden; transition: border-color 150ms ease, transform 150ms ease; }
.card:hover { border-color: color-mix(in oklch, var(--accent) 60%, var(--line)); transform: translateY(-1px); }
.card__link { display: block; color: inherit; text-decoration: none; }
.card__photo { aspect-ratio: 4 / 3; background: var(--surfaceAlt); display: grid; place-items: center; }
.card__photo img { width: 100%; height: 100%; object-fit: cover; }
.card__photo-placeholder { font-size: 3rem; color: var(--muted); font-weight: 700; }
.card__body { padding: 0.875rem; }
.card__title { margin: 0 0 0.25rem; font-size: 1rem; color: var(--ink); font-weight: 600; }
.card__summary { margin: 0 0 0.5rem; font-size: 0.875rem; color: var(--inkSoft); }
.card__meta { margin: 0; font-size: 0.8125rem; color: var(--muted); }
.card__price { font-weight: 600; color: var(--accent); }
</style>
